import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { fetchDealWithProgress } from '../../utils/dealMapper'
import { RsIcon, IconLabel } from '../icons'

const STAGE_LABELS = {
  posted: 'Launch',
  admin_review: 'Launch',
  matching: 'Launch',
  nda: 'Due Diligence',
  dd: 'Due Diligence',
  term_sheet: 'Negotiation / SPA',
  escrow: 'Closing',
  closed: 'ClosingPost',
}

const TRACKER_STAGES = [
  { key: 'posted', icon: 'upgrade', label: 'Launch' },
  { key: 'dd', icon: 'search', label: 'Due Diligence' },
  { key: 'term_sheet', icon: 'contracts', label: 'Negotiation / SPA' },
  { key: 'escrow', icon: 'fund', label: 'Closing' },
  { key: 'closed', icon: 'clipboard', label: 'ClosingPost' },
]

function trackerStageIndex(currentPhase) {
  const map = { posted: 1, admin_review: 1, matching: 1, nda: 2, dd: 2, term_sheet: 3, escrow: 4, closed: 5 }
  return map[currentPhase] || 1
}

function trackerStageStatus(stageKey, currentPhase) {
  const order = ['posted', 'dd', 'term_sheet', 'escrow', 'closed']
  const phaseToStage = {
    posted: 'posted',
    admin_review: 'posted',
    matching: 'posted',
    nda: 'posted',
    dd: 'dd',
    term_sheet: 'term_sheet',
    escrow: 'escrow',
    closed: 'closed',
  }
  const current = phaseToStage[currentPhase] || 'posted'
  const curIdx = order.indexOf(current)
  const stageIdx = order.indexOf(stageKey)
  if (curIdx > stageIdx) return 'completed'
  if (curIdx === stageIdx) return 'active'
  return 'pending'
}

function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`
  return `${bytes} B`
}

function formatDateLabel(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

function participantStatusClass(status) {
  if (status === 'active') return 'active'
  if (status === 'pending') return 'waiting'
  return 'idle'
}

export default function PortalGpInProgress({
  allDeals,
  selectedDeal,
  onSelectDeal,
  pageStats,
  loading,
  onExport,
  onAddTask,
}) {
  const activeDeals = allDeals.filter((d) => d.currentPhase !== 'closed')
  const [enrichedDeal, setEnrichedDeal] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const deal = enrichedDeal || selectedDeal || activeDeals[0] || null

  useEffect(() => {
    const target = selectedDeal || activeDeals[0]
    if (!target?.id) {
      setEnrichedDeal(null)
      return
    }
    let cancelled = false
    setDetailLoading(true)
    fetchDealWithProgress(axios, target.id)
      .then((full) => {
        if (!cancelled) setEnrichedDeal(full)
      })
      .catch(() => {
        if (!cancelled) setEnrichedDeal(target)
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedDeal?.id, activeDeals[0]?.id])

  const avgProgress = activeDeals.length
    ? Math.round(activeDeals.reduce((s, d) => s + (d.progress || 0), 0) / activeDeals.length)
    : 0
  const pendingStage = pageStats?.pendingActions ?? (deal?.pendingActions?.length || 0)
  const participantCount = deal?.participants?.length || (deal ? 1 : 0)
  const currentLabel = deal ? (STAGE_LABELS[deal.currentPhase] || deal.currentPhase) : '—'
  const stageNum = deal ? trackerStageIndex(deal.currentPhase) : 1

  const tasks = [
    ...(deal?.pendingActions || []).map((a, i) => ({
      id: a.id,
      title: a.title,
      meta: `Assigned: ${a.assignedRole || 'GP'} · Stage: ${currentLabel}`,
      done: false,
      variant: i === 0 ? 'urgent' : 'in-progress',
    })),
    ...(deal?.timeline || []).slice(0, 2).map((t, i) => ({
      id: `done-${i}`,
      title: t.event,
      meta: `Completed: ${formatDateLabel(t.date)}`,
      done: true,
    })),
  ]

  const documents = (deal?.dealFiles || []).map((f) => ({
    name: f.name,
    meta: `${f.uploader} · ${formatDateLabel(f.uploadedAt)} · ${formatFileSize(f.size)}`,
    badge: 'Uploaded',
    badgeStyle: 'success',
    icon: 'fileText',
    bg: 'rgba(15,61,138,.08)',
  }))

  const activity = (deal?.timeline || []).slice(0, 6).map((t) => ({
    action: t.event,
    time: formatDateLabel(t.date),
    icon: 'pin',
    bg: 'rgba(15,61,138,.1)',
  }))

  return (
    <div className="portal-gp-deals-page">
      <div className="portal-gp-topbar">
        <div className="portal-gp-topbar-title">
          <IconLabel name="progress">In Progress — Deal Progress Tracker</IconLabel>
        </div>
        <div className="portal-gp-topbar-right">
          <button type="button" className="portal-btn-outline" onClick={onExport}>
            <IconLabel name="calendar" size="xs">Export Report</IconLabel>
          </button>
          <button type="button" className="portal-btn-primary" onClick={onAddTask}>
            + Add Task
          </button>
        </div>
      </div>

      <div className="portal-gp-content">
        <div className="portal-gp-page-header">
          <h1>Global Deal Progress Center</h1>
          <p>All ongoing deals — overview at a glance (live data from your deals)</p>
        </div>

        {activeDeals.length > 1 && (
          <div className="portal-gp-deal-switcher">
            <span className="portal-gp-deal-switcher-label">Tracking deal:</span>
            <select
              className="portal-gp-deal-select"
              value={deal?.id || ''}
              onChange={(e) => {
                const picked = activeDeals.find((d) => String(d.id) === e.target.value)
                if (picked) onSelectDeal(picked)
              }}
            >
              {activeDeals.map((d) => (
                <option key={d.id} value={d.id}>{d.title}</option>
              ))}
            </select>
          </div>
        )}

        <div className="portal-overall-progress">
          <div className="portal-overall-stat">
            <div className="num">{loading ? '…' : activeDeals.length}</div>
            <div className="label">In Progress Deal</div>
          </div>
          <div className="portal-overall-stat">
            <div className="num">{loading ? '…' : pendingStage}</div>
            <div className="label">Pending This Stage</div>
          </div>
          <div className="portal-overall-stat">
            <div className="num">{detailLoading ? '…' : participantCount}</div>
            <div className="label">Participants</div>
          </div>
          <div className="portal-overall-bar-wrap">
            <div className="portal-overall-bar-label">
              <span>Overall Progress</span>
              <span>{loading ? '…' : `${avgProgress}%`}</span>
            </div>
            <div className="portal-overall-bar">
              <div className="portal-overall-bar-fill" style={{ width: `${loading ? 0 : avgProgress}%` }} />
            </div>
          </div>
        </div>

        {(loading || detailLoading) && <p className="portal-deal-table-empty">Loading deals…</p>}

        {!loading && !detailLoading && !deal && (
          <div className="portal-deal-table-empty">
            <p>No in-progress deals yet. Post a deal to start tracking.</p>
          </div>
        )}

        {!loading && !detailLoading && deal && (
          <>
            <div className="portal-progress-tracker">
              <div className="portal-tracker-header">
                <div className="portal-tracker-title">
                  Current Stage: {currentLabel}
                  <span className="portal-tracker-title-sub">Stage {stageNum} / 5</span>
                </div>
                <div className="portal-tracker-header-meta">Target: {deal.target || '—'}</div>
              </div>
              <div className="portal-tracker-stages">
                {TRACKER_STAGES.map((stage) => {
                  const status = trackerStageStatus(stage.key, deal.currentPhase)
                  return (
                    <div key={stage.key} className={`portal-tracker-stage ${status}`}>
                      <div className="portal-tracker-dot"><RsIcon name={stage.icon} size="sm" /></div>
                      <div className="portal-tracker-label">{stage.label}</div>
                      <div className="portal-tracker-stage-meta">
                        {status === 'completed' ? 'Completed' : status === 'active' ? 'In Progress' : 'Pending'}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="portal-deal-detail-grid">
              <div className="portal-detail-card">
                <div className="portal-detail-card-header">
                  <div className="portal-detail-card-title">
                    <IconLabel name="users" size="xs">Participants ({participantCount})</IconLabel>
                  </div>
                </div>
                <div className="portal-participant-list">
                  {(deal.participants || []).length === 0 ? (
                    <p className="portal-deal-table-empty">No participants yet — LPs can join from Browse.</p>
                  ) : (
                    deal.participants.map((p) => (
                      <div key={`${p.role}-${p.name}`} className="portal-participant-item">
                        <div className="portal-participant-avatar-lg" style={{ background: '#0f3d8a' }}>
                          {(p.role || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="name">{p.name}</p>
                          <p className="role">{p.label || p.role}</p>
                        </div>
                        <span className={`portal-status-pill ${participantStatusClass(p.status)}`}>
                          {p.status === 'active' ? 'Active' : p.status === 'pending' ? 'Pending' : 'Inactive'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="portal-detail-card">
                <div className="portal-detail-card-header">
                  <div className="portal-detail-card-title">
                    <IconLabel name="clipboard" size="xs">Current Stage Tasks</IconLabel>
                  </div>
                </div>
                <div className="portal-task-list portal-task-list-rich">
                  {tasks.length === 0 ? (
                    <p className="portal-deal-table-empty">No tasks yet. Use &quot;+ Add Task&quot; to create one.</p>
                  ) : (
                    tasks.map((task) => (
                      <div key={task.id} className={`portal-task-item-rich ${task.done ? 'done' : task.variant}`}>
                        <div className="portal-task-checkbox">{task.done ? '✓' : ''}</div>
                        <div className="portal-task-body">
                          <div className="portal-task-title">{task.title}</div>
                          <p className="portal-task-meta">{task.meta}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="portal-deal-detail-grid">
              <div className="portal-detail-card">
                <div className="portal-detail-card-header">
                  <div className="portal-detail-card-title">
                    <IconLabel name="files" size="xs">Deal Documents ({documents.length})</IconLabel>
                  </div>
                </div>
                <div className="portal-doc-list">
                  {documents.length === 0 ? (
                    <p className="portal-deal-table-empty">No files linked to this deal yet. Upload from Files.</p>
                  ) : (
                    documents.map((doc) => (
                      <div key={doc.name} className="portal-doc-item">
                        <div className="portal-doc-icon" style={{ background: doc.bg }}><RsIcon name={doc.icon} size="sm" /></div>
                        <div className="portal-doc-info">
                          <div className="portal-doc-name">{doc.name}</div>
                          <div className="portal-doc-meta">{doc.meta}</div>
                        </div>
                        <span className={`portal-doc-badge ${doc.badgeStyle}`}>{doc.badge}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="portal-detail-card">
                <div className="portal-detail-card-header">
                  <div className="portal-detail-card-title">
                    <IconLabel name="activity" size="xs">Activity Timeline</IconLabel>
                  </div>
                </div>
                <div className="portal-activity-list portal-activity-list-rich">
                  {activity.length === 0 ? (
                    <p className="portal-deal-table-empty">No activity recorded yet.</p>
                  ) : (
                    activity.map((item, i) => (
                      <div key={i} className="portal-activity-item">
                        <div className="portal-activity-dot" style={{ background: item.bg }}><RsIcon name={item.icon} size="xs" /></div>
                        <div className="portal-activity-body">
                          <div className="portal-activity-text">{item.action}</div>
                          <div className="portal-activity-time">{item.time}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}












